import { DEFAULT_LOCALE, ENV } from '@/constants';

/**
 * Localizes URLs in a schema object by adding locale prefix
 * @param schema - Schema object (JSON-LD)
 * @param locale - Current locale
 * @param defaultLocale - Default locale (from constants)
 * @param baseUrl - Base URL (defaults to ENV.SEO_URL)
 * @returns Localized schema object
 */
export const localizeSchema = (
	schema: any,
	locale: string,
	defaultLocale = DEFAULT_LOCALE,
	baseUrl?: string,
): any => {
	if (!schema || typeof schema !== 'object') {
		return schema;
	}

	const normalizedBaseUrl = baseUrl || ENV.SEO_URL || 'https://liftapp.ai';
	const localePrefix = locale !== defaultLocale ? `/${locale}` : '';

	// Recursively process the schema object
	const processValue = (value: any): any => {
		if (value === null || value === undefined) {
			return value;
		}

		// If it's a string URL that contains the base URL, localize it
		if (typeof value === 'string') {
			// Check if it's a URL that needs localization
			if (value.startsWith(normalizedBaseUrl) && locale !== defaultLocale) {
				// Check if URL already contains any locale prefix
				const urlAfterBase = value.substring(normalizedBaseUrl.length);
				const hasLocalePrefix = /^\/[a-z]{2}(\/|$)/.test(urlAfterBase);

				// Only localize if URL doesn't already have a locale prefix
				if (!hasLocalePrefix) {
					// Replace base URL with localized version
					return value.replace(
						normalizedBaseUrl,
						`${normalizedBaseUrl}${localePrefix}`,
					);
				}
			}
			return value;
		}

		// If it's an array, process each element
		if (Array.isArray(value)) {
			return value.map(processValue);
		}

		// If it's an object, process recursively
		if (typeof value === 'object') {
			const processed: Record<string, any> = {};
			// eslint-disable-next-line no-restricted-syntax
			for (const [key, val] of Object.entries(value)) {
				processed[key] = processValue(val);
			}
			return processed;
		}

		return value;
	};

	const localizedSchema = processValue(schema);

	// Add inLanguage property to WebPage objects if not present
	if (localizedSchema['@graph'] && Array.isArray(localizedSchema['@graph'])) {
		localizedSchema['@graph'] = localizedSchema['@graph'].map((item: any) => {
			if (item['@type'] === 'WebPage' && !item.inLanguage) {
				return {
					...item,
					inLanguage: locale,
				};
			}
			return item;
		});
	} else if (
		localizedSchema['@type'] === 'WebPage' &&
		!localizedSchema.inLanguage
	) {
		localizedSchema.inLanguage = locale;
	}

	return localizedSchema;
};
